/**
 * Tesoreria › Chiusura dell'anno (3.10, docs/analisi/proposta-da-sistemare-chiusura-anno.md
 * §5, ADR-029): the checks of a year, which admit an exception with its
 * reason, and the closure with the snapshot of the year.
 */
import { z } from 'zod';
import { addDays } from './dates.js';
import { toCents } from './money.js';
export const YEAR_CHECKS = [
    'estratti',
    'movimenti',
    'saldi',
    'spese_carta',
    'file_carte',
    'fatture',
    'autofatture',
    'quadratura',
];
/** Movements and card spending are all reconciled: no exception (decision of 30/10/2026). */
export const MANDATORY_CHECKS = [
    'estratti',
    'movimenti',
    'saldi',
    'spese_carta',
];
export const YEAR_CHECK_LABELS = {
    estratti: 'Estratti conto caricati',
    movimenti: 'Movimenti di banca',
    saldi: 'Saldi uguali a quelli della banca',
    spese_carta: 'Spese con carta',
    file_carte: 'File delle carte',
    fatture: 'Fatture classificate',
    autofatture: 'Autofatture collegate al pagamento',
    quadratura: 'Quadratura con i mastrini',
};
/** The SQLSTATE of the triggers that keep a closed year as it is. */
export const YEAR_CLOSED_ERROR = 'KM001';
/** The shortest reason of an exception. */
export const EXCEPTION_NOTE_MIN = 10;
/** The days of the range no period covers, as ranges; periods without dates are ignored. */
export function coverageGaps(range, periods) {
    const sorted = periods
        .filter((period) => Boolean(period.from && period.to))
        .sort((a, b) => a.from.localeCompare(b.from));
    const gaps = [];
    let next = range.from;
    for (const period of sorted) {
        if (next > range.to)
            break;
        if (period.to < next)
            continue;
        if (period.from > next) {
            const end = addDays(period.from, -1);
            gaps.push({ from: next, to: end < range.to ? end : range.to });
        }
        next = addDays(period.to, 1);
    }
    if (next <= range.to)
        gaps.push({ from: next, to: range.to });
    return gaps;
}
/**
 * The days of the range no statement covers and no balance of the bank
 * explains: a gap is fine when the closing balance of the bank before it is
 * the opening balance after it (nothing moved). `start` is the opening balance
 * of the account, for a gap at its first day. When the bank gives only a closing
 * after the gap (a head of Intesa, 3.20), the balance before it plus the
 * movements must reach that closing: a missing movement would not.
 */
export function unexplainedGaps(range, periods, balances, start, movements = []) {
    const days = [...balances].sort((a, b) => a.date.localeCompare(b.date));
    const covered = [...periods, ...days.map((day) => ({ from: day.date, to: day.date }))];
    return coverageGaps(range, covered).filter((gap) => {
        const before = days.filter((day) => day.date < gap.from && day.closing !== null).at(-1);
        const after = days.find((day) => day.date > gap.to && day.opening !== null);
        const closing = before?.closing ?? (start && start.date <= gap.from ? start.amount : null);
        if (closing !== null && after && toCents(closing) === toCents(after.opening))
            return false;
        if (closing === null)
            return true;
        const next = days.find((day) => day.date > gap.to && day.closing !== null);
        if (!next)
            return true;
        // From the day after the last closing, or from the first day of the opening of the account.
        const moved = movements
            .filter((item) => (before ? item.date > before.date : item.date >= start.date) && item.date <= next.date)
            .reduce((sum, item) => sum + toCents(item.amount), 0);
        return toCents(closing) + moved !== toCents(next.closing);
    });
}
/**
 * The day of a payment given now from money that moved in a closed year (a
 * payment without a document used on an invoice, 3.10): the closed year is not
 * touched, the payment goes on the invoice's day, never before the first day of
 * the first open year.
 */
export function settlementDateAfterClosing(sourceDate, invoiceDate, closedYears) {
    let year = Number(sourceDate.slice(0, 4));
    if (!closedYears.has(year))
        return sourceDate;
    while (closedYears.has(year))
        year += 1;
    const firstOpen = `${year}-01-01`;
    return invoiceDate > firstOpen ? invoiceDate : firstOpen;
}
/** The months (1–12) of the year without a covered day. */
export function monthsWithout(year, periods) {
    const months = [];
    for (let month = 1; month <= 12; month += 1) {
        const from = `${year}-${String(month).padStart(2, '0')}-01`;
        const to = addDays(month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`, -1);
        const gaps = coverageGaps({ from, to }, periods);
        if (gaps.length === 1 && gaps[0].from === from && gaps[0].to === to)
            months.push(month);
    }
    return months;
}
/** What keeps a year from closing; empty when it can close. */
export function closingProblems(checks, exceptions) {
    const problems = [];
    for (const check of checks) {
        if (check.ok)
            continue;
        const label = YEAR_CHECK_LABELS[check.key];
        if (MANDATORY_CHECKS.includes(check.key)) {
            problems.push(`Controllo obbligatorio non superato: ${label}`);
            continue;
        }
        const note = exceptions.find((item) => item.check === check.key)?.note.trim() ?? '';
        if (note.length < EXCEPTION_NOTE_MIN)
            problems.push(`Manca il motivo per: ${label}`);
    }
    return problems;
}
const year = z.number().int().min(2000).max(2100);
export const yearCloseSchema = z.object({
    year,
    exceptions: z
        .array(z.object({
        check: z.enum(YEAR_CHECKS),
        note: z.string().trim().min(EXCEPTION_NOTE_MIN).max(500),
    }))
        .max(YEAR_CHECKS.length),
});
export const yearReopenSchema = z.object({
    reason: z.string().trim().min(10, { message: 'Scrivi il motivo (almeno 10 caratteri)' }).max(500),
    /** The year written again by who reopens it. */
    confirm: year,
});
export const GESTIFY_CLOSED_CHANGES = ['nuova', 'cambiata', 'tolta'];
//# sourceMappingURL=year-closing.js.map